import React, { forwardRef, useId, useState } from "react";
import { m as motion } from "framer-motion";
import Link, { LinkProps } from "@components/link";
import { cn } from "@/helpers/classNames";

export type AccessoryLinkProps = {
  // eslint-disable-next-line react/require-default-props
  to?: LinkProps["to"];
} & (
  | React.HTMLAttributes<HTMLButtonElement>
  | Omit<LinkProps, "to" | "type" | "onPointerEnter" | "onPointerLeave">
);

const AccessoryLink = forwardRef<
  HTMLAnchorElement | HTMLButtonElement,
  AccessoryLinkProps
>(
  (
    { to = undefined, children: passedChildren, className, ...passedProps },
    ref,
  ) => {
    const id = useId();
    const [hovered, setHovered] = useState(false);

    const props = {
      className: cn(
        "group/accessory rounded font-medium font-display leading-tight py-1f inline-flex items-center jusitfy-center",
        className,
      ),
      ...passedProps,
    };

    const children = (
      <>
        <span>{passedChildren}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="28"
          height="18"
          viewBox="0 0 28 18"
          className="w-auto ml-2f h-5f"
        >
          <g fill="none" fillRule="evenodd">
            <motion.rect
              mask={`url(#${id}-arrow)`}
              fill="currentColor"
              x="0"
              y="0"
              width="24"
              height="18"
              rx="9"
              ry="9"
              variants={{
                idle: { width: 24 },
                hover: { width: 28 },
              }}
              animate={hovered ? "hover" : "idle"}
              transition={{ ease: "easeOut", duration: 0.2 }}
              initial="idle"
            />
            <mask id={`${id}-arrow`}>
              <rect x="0" y="0" width="28" height="18" fill="#fff" />
              <motion.g
                variants={{
                  idle: { translateX: 0 },
                  hover: { translateX: 2 },
                }}
                animate={hovered ? "hover" : "idle"}
                initial="idle"
              >
                <path
                  fill="#000"
                  fillRule="nonzero"
                  d="M8.98437471,0.181549036 L13.7968747,4.77529904 C14.0677084,5.03382213 14.0677084,5.46618009 13.7968747,5.72470319 L8.98437471,10.3184532 C8.72220399,10.5687071 8.30680179,10.5590465 8.05654792,10.2968758 C7.80629406,10.0347051 7.81595457,9.6193029 8.07812529,9.36904904 L11.706,5.906 L0.65625,5.90625111 C0.293813133,5.90625111 0,5.61243798 0,5.25000111 C0,4.88756425 0.293813133,4.59375111 0.65625,4.59375111 L11.705,4.593 L8.07812529,1.13095319 C7.83978828,0.903449673 7.81013738,0.539459931 7.99473547,0.278187432 L8.05654792,0.203126403 C8.30680179,-0.0590443138 8.72220399,-0.0687048301 8.98437471,0.181549036 Z"
                  transform={`translate(5.75 4.25) scale(0.9) ${
                    /^\/(?!\/)/.test(to ?? "") ? "" : "rotate(-45 7 5.25)"
                  }`}
                />
              </motion.g>
            </mask>
          </g>
        </svg>
      </>
    );

    if (to) {
      return (
        <Link
          ref={ref as React.ForwardedRef<HTMLAnchorElement>}
          to={to}
          onPointerEnter={() => setHovered(true)}
          onPointerLeave={() => setHovered(false)}
          {...(props as Omit<LinkProps, "to">)}
        >
          {children}
        </Link>
      );
    }

    return (
      <button
        type="button"
        ref={ref as React.Ref<HTMLButtonElement>}
        onPointerEnter={() => setHovered(true)}
        onPointerLeave={() => setHovered(false)}
        {...(props as React.HTMLAttributes<HTMLButtonElement>)}
      >
        {children}
      </button>
    );
  },
);

export default AccessoryLink;
